import { formatDate } from '@angular/common';
import { BookingSimple } from '@app/utils/models/booking-simple.model';
import { PublicStatusType } from '@app/utils/models/public-status-type.model';

const UPCOMING_PUBLIC_STATUS_TYPES: PublicStatusType[] = [
  PublicStatusType.BUILDING,
  PublicStatusType.PLANNING,
  PublicStatusType.READY,
  PublicStatusType.WAITING,
];

// Requested and upcoming trips are sorted soonest end date first, previous trips most recent first.
// Trips without an end date sort as the earliest.
export function getRequestedTrips(bookingSimples: BookingSimple[]): BookingSimple[] {
  const cutoffDateString = getCutoffDateString();
  return bookingSimples
    .filter(bookingSimple => isRequestedTrip(bookingSimple) && !hasEnded(bookingSimple, cutoffDateString))
    .sort(compareByEndDate);
}

export function getUpcomingTrips(bookingSimples: BookingSimple[]): BookingSimple[] {
  const cutoffDateString = getCutoffDateString();
  return bookingSimples
    .filter(bookingSimple => isUpcomingTrip(bookingSimple) && !hasEnded(bookingSimple, cutoffDateString))
    .sort(compareByEndDate);
}

export function getPreviousTrips(bookingSimples: BookingSimple[]): BookingSimple[] {
  const cutoffDateString = getCutoffDateString();
  return bookingSimples
    .filter(bookingSimple => !isRequestedTrip(bookingSimple) && hasEnded(bookingSimple, cutoffDateString))
    .sort((bookingSimpleA, bookingSimpleB) => compareByEndDate(bookingSimpleB, bookingSimpleA));
}

export function canNavigateTrip(publicStatusType: PublicStatusType): boolean {
  return publicStatusType !== PublicStatusType.FUTURE && publicStatusType !== PublicStatusType.BUILDING;
}

// Classified by public status only: a BookingReady refresh patches publicStatus but not statusType.
function isRequestedTrip(bookingSimple: BookingSimple): boolean {
  return bookingSimple.publicStatus.value === PublicStatusType.FUTURE;
}

function isUpcomingTrip(bookingSimple: BookingSimple): boolean {
  return !isRequestedTrip(bookingSimple) && UPCOMING_PUBLIC_STATUS_TYPES.includes(bookingSimple.publicStatus.value);
}

// A trip without an end date never ends.
function hasEnded(bookingSimple: BookingSimple, cutoffDateString: string): boolean {
  return bookingSimple.endDate !== null && bookingSimple.endDate < cutoffDateString;
}

function compareByEndDate(bookingSimpleA: BookingSimple, bookingSimpleB: BookingSimple): number {
  return (bookingSimpleA.endDate ?? '').localeCompare(bookingSimpleB.endDate ?? '');
}

// Yesterday, so a trip still counts as ongoing on the day after it ends. Dates are compared as
// yyyy-MM-dd strings, which sort chronologically.
function getCutoffDateString(): string {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - 1);
  return formatDate(cutoffDate, 'yyyy-MM-dd', 'en');
}
